import { Injectable } from "@angular/core";
import { GooglePlus } from "@awesome-cordova-plugins/google-plus/ngx";

import { AppService } from "../../app.service";
import { BehaviorSubject } from "rxjs";
import { WHITELIST_DOMAINS } from "../../auth.config";

import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFireDatabase } from "@angular/fire/compat/database";
import { getAuth, User, UserInfo } from "firebase/auth";
import {
  AuthCredential,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { AlertService } from "../alerts/alerts.service";

import {
  AppAuth,
  AppUser,
  isUserWithEmail,
  UserWithEmail,
} from "./app-auth-types";

import { console } from "#/log";
import firebase from "firebase/compat/app";

interface SignInTokenResult extends Record<string, any> {
  provider: string;
}

interface SignInGoogleTokenResult extends SignInTokenResult {
  userId: string;
  displayName: string;
  imageUrl: string;
  email: string;
  familyName: string;
  givenName: string;
  idToken: string;
}

interface SignInAppleTokenResult extends SignInTokenResult {
  email: string;
  authorizationCode: string;
  state: string;
  identityToken: string;
  fullName: {
    nickname: string;
    phoneticRepresentation: {};
    middleName: string;
    familyName: string;
    namePrefix: string;
    givenName: string;
    nameSuffix: string;
  };
  user: string;
}

function errorHasLocalizedDescription(
  error: any,
): error is { localizedDescription: string } {
  return (
    typeof error === "object" &&
    error !== null &&
    typeof error.localizedDescription === "string"
  );
}

export class ErrorOAuthCancelled extends Error {
  public readonly errorOAuthCancelled = true;
  public static is(error: any): error is ErrorOAuthCancelled {
    return error.errorOAuthCancelled !== undefined;
  }
  constructor(message?: string) {
    super();
    if (!message) message = "User cancelled third-party authentication.";
  }
}

/**
 * A Service responsible for handling all user authentication related
 * tasks.
 */
@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  public loggingIn: boolean = false;
  private validDomains: Array<string> = WHITELIST_DOMAINS;
  private fullAccessGranted: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  private userSubject = new BehaviorSubject<AppUser | null>(null);

  /**
   * Convenience wrapper for debug-logging properties of the Firebase UserInfo
   * without being excessively spammy
   * @param user firebase.UserInfo
   * @returns {string | object} An object to log
   */
  public static debugUser(user: AppUser | null): string | object {
    const stringify = false;
    return stringify
      ? AppService.safeStringify(user)
      : user === null
        ? "[null]"
        : "email" in user || "uid" in user
          ? JSON.stringify({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
            })
          : user;
  }

  auth: AngularFireAuth;

  constructor /***
   * sph 2024-10-24 stub firebase auth support
   ***/(
    private af: AngularFireAuth,
    private appService: AppService,
    private afd: AngularFireDatabase,
    private googlePlus: GooglePlus,
    private alertService: AlertService,
  ) {
    this.auth = this.af;

    this.auth.onAuthStateChanged((firebaseUser: any) => {
      console.debug("onAuthStateChanged: raw user:", firebaseUser);
      const appUser = firebaseUser ? this.toAppUser(firebaseUser) : null;
      this.validateAuth(appUser);
    });

    /***
     * sph 2024-10-24 stub firebase auth support
     ***/
    this.afd
      .list("/allowed-domains")
      .valueChanges()
      .subscribe((list: any[]) => {
        this.validDomains = list.map((item: string) => {
          return item + "";
        });
        console.debug("auth: loaded valid login domains", this.validDomains);
      });

    //subscribe to if/when the full access is purchased
    this.fullAccessGranted.subscribe((granted) => {
      console.debug("auth: received new value: " + granted);
    });
  }

  /***
   * sph 2024-10-24 stub firebase auth support
   ***/
  public getAuth(): AngularFireAuth {
    return this.auth;
  }

  public getCurrentUser(): User | null {
    const auth = getAuth();
    return auth.currentUser;
  }

  /**
   * Handles OAuth signin for mobile / browser
   */
  public async signInWithOAuth(provider: string): Promise<any> {
    console.debug(
      `AuthenticationService.signInWithOAuth: provider: '${provider}'`,
    );
    /***
     * sph 2024-10-24 stub firebase auth support
     ***/

    this.af
      .signOut()
      .then(() => {
        console.debug("User signed out successfully");
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
    this.loggingIn = true;

    if (this.appService.isMobileApp()) {
      let credential = null;

      try {
        credential = await this.signInWithNative(provider);
        if (!credential) {
          throw new Error("Failed to get OAuth credential.");
        }
      } catch (error) {
        if (ErrorOAuthCancelled.is(error)) {
          console.debug(
            `AuthenticationService.signInWithOAuth: suppressing 'user-canceled' error return`,
          );
          throw (error as Error).message; // Ensure the error is properly typed
        }

        this._handleAuthError(
          "signInWithOAuth -> native-plugin OAuth credential flow",
          error as Error,
        );
      }

      try {
        if (credential) {
          const signInResult = await this.af.signInWithCredential(credential);
          console.debug(
            `AuthenticationService.signInWithOAuth: signInWithCredential() returned ${AppService.safeStringify(
              signInResult,
            )}`,
          );
        }

        const currentUser = await this.af.currentUser;

        if (currentUser) {
          const appUser: AppUser = {
            ...currentUser,
            providerData: currentUser.providerData.filter(
              (p): p is UserInfo => p !== null,
            ), // Remove nulls
          };
          const r = await this.validateAuth(appUser);
          console.debug(
            `AuthenticationService.signInWithOAuth: validateAuth() returned ${AppService.safeStringify(
              r,
            )}`,
          );
          return r;
        } else {
          console.warn("No user is currently signed in.");
          return null;
        }
      } catch (error) {
        this._handleAuthError("signInWithCredential", error as Error);
      }
    } else {
      // return this._signInWithBrowser(provider)
      //   .then(this.validateAuth.bind(this))
      //   .catch(this._handleAuthError.bind(this, "oAuth -> signInWithBrowser"));
    }
  }

  /**
   * oAuth using ionic-native plugins with cordova
   * throws ErrorOAuthCancelled if the user cancels the flow
   */
  public async signInWithNative(
    provider: "google.com" | "apple.com" | string,
  ): Promise<AuthCredential> {
    try {
      switch (provider) {
        case "google.com": {
          // const { idToken } = await this._signInWithGoogle(provider);
          let idToken = "FAKE GOOGLE TOKEN";
          if (!idToken) throw new Error("No ID token received from Google");
          return GoogleAuthProvider.credential(idToken);
        }
        case "apple.com": {
          // const { identityToken } = await this._signInWithApple(provider);
          let identityToken = "FAKE APPLE TOKEN";
          if (!identityToken)
            throw new Error("No identity token received from Apple");
          return new OAuthProvider("apple.com").credential({
            idToken: identityToken,
          });
        }
        default:
          throw new Error("Unsupported provider: " + provider);
      }
    } catch (error: unknown) {
      let canceled = false;

      // First, check if the error is an instance of Error
      if (error instanceof Error) {
        switch (provider) {
          case "google.com":
            if (
              typeof error.message === "string" &&
              error.message.includes("user canceled")
            ) {
              canceled = true;
            }
            if (typeof error === "number" && error === 12501) {
              canceled = true;
            }
            break;

          case "apple.com":
            if (
              "code" in error &&
              typeof error.code === "string" &&
              error.code === "1001"
            ) {
              canceled = true;
            }
            break;
        }

        if (canceled) {
          throw new ErrorOAuthCancelled();
        }

        // Type guard to check if error has localizedDescription
        if (errorHasLocalizedDescription(error)) {
          error.message = error.localizedDescription;
        }
        throw error;
      } else {
        // If the error is not an instance of Error, just throw it
        throw new Error("An unknown error occurred during OAuth sign-in");
      }
    }
  }

  /**
   * Handles signing with username and password
   */
  public async signInWithEmailPassword(
    email: string,
    password: string,
  ): Promise<any> {
    this.loggingIn = true;

    try {
      const userCredential = await this.af.signInWithEmailAndPassword(
        email,
        password,
      );
      const user = userCredential.user; // Get the signed-in user
      if (!user) {
        throw new Error("User not found after authentication");
      }
      const appUser: AppUser = {
        ...user,
        providerData: user.providerData.filter(
          (p): p is UserInfo => p !== null,
        ), // Remove nulls
      };
      // return this.validateAuth(appUser); // Validate auth with the user
      return null;
    } catch (error) {
      return this._handleAuthError("signInWithEmailPassword", error as Error);
    }
  }

  /**
   * Validates the user object to make sure the user is allowed
   * to access the app.
   * No actual validation occurs at this time; this just sets the user.
   * For future use.
   */
  public validateAuth(user: AppUser | null): AppUser | null {
    console.debug(
      `AuthenticationService.validateAuth(): user: ${AuthenticationService.debugUser(
        user,
      )}; setting user.`,
    );

    if (user !== null && !isUserWithEmail(user)) {
      console.warn(
        "AuthenticationService.validateAuth(): isUserWithEmail() check fail:",
        AuthenticationService.debugUser(user),
      );
    }

    console.log(
      "AuthenticationService.validateAuth(): user email:",
      user?.email,
    );
    this._setUser(user);

    setTimeout(() => {
      // HACK: Fixes login on web version w/ oauth
      this.loggingIn = false;
    });

    return user;
  }

  public getUser(): UserWithEmail {
    const user = this.userSubject.value;
    console.log("AuthenticationService.getUser(): user:", user);
    if (!isUserWithEmail(user)) {
      throw new Error("Unexpected incomplete user");
    }
    return user;
  }

  // Normalize the Firebase User into AppUser type
  private toAppUser(firebaseUser: User | null): AppUser | null {
    if (!firebaseUser) return null;
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      providerData: firebaseUser.providerData.filter(
        (p): p is UserInfo => p !== null,
      ),
    };
  }

  /**
   * Returns whether the user has full sample access or not based on their
   * email address.
   */
  public hasFullAccess(): boolean {
    let allowed = this.fullAccessGranted.value;
    const user = this.userSubject.value;
    console.log("AuthenticationService.hasFullAccess(): user:", user);

    if (user && user.email) {
      for (const domain of this.validDomains) {
        allowed = allowed || user.email.includes(domain);
      }
    }
    return allowed;
  }

  /**
   * Provides full access to a user when all samples are purchased
   */
  provideFullAccess(access: boolean) {
    this.fullAccessGranted.next(access);
  }

  /**
   * Set user in this singleton to be used thru the app
   */
  private _setUser(user: AppUser | null): void {
    console.debug("AuthenticationService._setUser(): user:", user);
    this.userSubject.next(user);
  }

  /**
   * Registers a new user with email and password
   */
  public async registerEmailAccount(
    email: string,
    password: string,
  ): Promise<any> {
    return this.af.createUserWithEmailAndPassword(email, password);
  }

  /**
   * Deletes the currently signed-in user
   */
  public async deleteCurrentUserAccount(): Promise<any> {
    const user = await this.af.currentUser;
    if (!user) {
      throw new Error("deleteCurrentUserAccount(): no current user");
    }
    return user.delete(); // ✅ Use compat API method
  }

  // /**
  //  * Sends a reset email.  If the email address has an account associated
  //  * with it, then that email address is sent down the promise chain.  If
  //  * there is no account associated with it, an error alert is shown.
  //  */
  public resetPassword(email: string): Promise<any> {
    return this.af
      .sendPasswordResetEmail(email)
      .then(() => email)
      .catch((e) => this.alertService.showAlert("reset_error", [e.message]));
  }

  // /**
  //  * Removes the authentication token and logs out the user.
  //  */
  public signOut(): Promise<void> {
    return this.af.signOut();
    /* if (this.appService.isMobileApp()) {
      GooglePlus.disconnect();
      cordova.plugins.SignInWithApple.signOut() - revoke SIWA token if applicable?
    } */
  }

  /**
   * Login using browser based OAuth for the passed provider.
   */
  // private _signInWithBrowser(provider: string): Promise<any> {
  //   let providerObj: AuthProvider;
  //   switch (provider) {
  //     case "google.com":
  //       providerObj = new firebase.auth.GoogleAuthProvider();
  //       break;
  //     case "apple.com":
  //       providerObj = _newFirebaseAuthOAuthProvider("apple.com");
  //       break;
  //     default:
  //       throw Error("Unsupported provider: " + provider);
  //   }
  //   return this.af.auth
  //     .signInWithRedirect(providerObj)
  //     .then(() => this.af.auth.getRedirectResult());
  // }

  /**
   * Wrap Apple OAuth ("Sign In With Apple") details
   */
  public async signInWithApple() {
    try {
      const provider = new firebase.auth.OAuthProvider("apple.com");
      const result = await this.af.signInWithPopup(provider);
      console.log("Firebase Apple Sign-In Success:", result);
      return result;
    } catch (error) {
      console.error("Firebase Apple Sign-In Failed:", error);
      throw error;
    }
  }

  /**
   * Wrap Google OAuth signin details
   * @param provider "google.com"
   * @returns
   */
  public async signInWithGoogle() {
    try {
      const result = await this.af.signInWithPopup(
        new firebase.auth.GoogleAuthProvider(),
      );
      console.log("Google Sign-In Success:", result);
      return result;
    } catch (error) {
      console.error("Google Sign-In Failed:", error);
      throw error;
    }
  }

  public invalidFormatError() {
    this.alertService.showAlert("invalid_login");
  }

  /**
   * Shows an error in an alert.
   */
  private _handleAuthError(from: string, e: Error): void {
    console.debug(
      `Authentication error (from: '${from}'):`,
      e,
      AppService.safeStringify(e),
    );
    this.loggingIn = false;

    this.alertService.showAlert("login_failed", [e.message || e.toString()]);
    throw e.message;
  }
}
